import { Component, createRef } from 'react';

import { GenericMouseEvent } from '../../../types/types';
import { UIUtils } from '../../../utils/dom-utils';
import { WindowUtils } from '../../../utils/window.utils';
import style from './seekbar.component.module.scss';


interface SeekbarProps {
    max?: number;
    min?: number;
    start?: number;
    step?: number;
    emitInitialValueOnInit: boolean;
    onChange?: (value: number) => void;
    onChangeEnd?: (value: number) => void;
}

interface SeekbarState {
    value: number;
    left: number;
}

export default class Seekbar extends Component<SeekbarProps, SeekbarState> {
    container = createRef<HTMLDivElement>();
    handler: ((e: GenericMouseEvent) => void) | null = null;
    eventOffset = 0;

    max = 100;
    min = 0;
    step = 1;

    valueFormatter = (val: number) => val;

    readonly state: Readonly<SeekbarState> = this.initializeAndGetState(this.props);

    constructor(props: SeekbarProps) {
        super(props);
    }

    initializeAndGetState(props: SeekbarProps) {
        this.max = props.max || 100;
        this.min = props.min || 0;
        this.step = props.step || 1;

        const stepStr = `${this.step}`;
        if (stepStr.indexOf('.') > -1) {
            const numberOfDecimals = stepStr.length - (stepStr.indexOf('.') + 1);
            this.valueFormatter = (val) => {
                const parsedVal = val.toFixed(numberOfDecimals);
                return Number(parsedVal);
            };
        }

        const startOffset = 100 * ((props.start || 0) - this.min) / (this.max - this.min)
        return {
            value: props.start || 0,
            left: startOffset,
        }
    }

    componentDidMount() {
        this.notifyCallbacks(true);
    }

    handleMousedown(e: MouseEvent | any) {
        this.handler = (ev: any) => this.handleMouseMove(ev);
        const event = UIUtils.toGenericMouseEvent(e);
        if (event) {
            this.eventOffset = event.offsetX;
            WindowUtils.addMoveMoveHandlerToWindow(this.handler, true);
        }
    }

    handleMouseMove(event: GenericMouseEvent) {
        if (event) {
            this.updateHandlerPositionAndNotify(event.pageX);
        }
    }

    updateHandlerPositionAndNotify(pageX: number, notify = true) {
        const rail: HTMLElement = this.container.current.querySelector(`.${style.rail}`) as HTMLElement;
        const railRect = rail.getBoundingClientRect();
        let handlerLeft = pageX - (railRect.left + this.eventOffset);
        if (handlerLeft < -9) {
            handlerLeft = -9;
        } else if (handlerLeft > railRect.width - 9) {
            handlerLeft = railRect.width - 9;
        }
        const absSeekPercentage = (handlerLeft) / railRect.width;
        const seekPercentage = (handlerLeft + 9) / railRect.width;
        const readablePosition = seekPercentage * (this.max - this.min) + this.min;

        const offsetFromClosestLowerStep = (readablePosition - this.min) % this.step;
        const offsetToClosestStep = offsetFromClosestLowerStep >= this.step / 2 ?
            this.step - offsetFromClosestLowerStep : -1 * offsetFromClosestLowerStep;

        const value = this.valueFormatter(readablePosition + offsetToClosestStep);
        const left = absSeekPercentage * 100;
        this.updateState(value, left);
        this.notifyCallbacks(notify);
    }

    notifyCallbacks(notify: boolean) {
        if (this.props.onChange && notify) {
            this.props.onChange(this.state.value);
        }
        if (this.props.onChangeEnd && notify) {
            this.props.onChangeEnd(this.state.value);
        }
    }

    updateState(value: number, leftPercentage: number) {
        if (value !== this.state.value) {
            this.setState({
                value: value,
                left: leftPercentage,
            });
        }
    }

    render() {
        return (
            <div ref={this.container} className={style.container}>
                <div className={style.state}>
                    <div className={style.min}>{this.min}</div>
                    <div className={style.value}>{this.state.value}</div>
                    <div className={style.max}>{this.max}</div>
                </div>
                <div className={style['range-selector']}>

                    <div className={style.rail} >
                        <div className={style['rail-progress']} style={{ width: `${this.state.left}%`, }}></div>
                        <span className={style.handler}
                            style={{ left: `${this.state.left}%` }}
                            onTouchStart={(e) => this.handleMousedown(e)}
                            onMouseDown={(e) => this.handleMousedown(e)}></span>
                    </div>
                </div>
            </div>
        );
    }
}